import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, UserPlus, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { EmptyState, adminInputCls } from "@/components/admin/AdminBits";

export function StaffPanel() {
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/staff");
        setStaff(data);
      } catch (error) {
        console.error("Staff load failed:", error);
        toast.error("Failed to load staff");
      }
    })();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/staff", f);
      setStaff((ls) => [data, ...ls]);
      setF({ name: "", email: "", password: "" });
      toast.success("Staff account created");
    } catch (err) {
      console.error("Staff create failed:", err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this staff member's access?")) return;
    try {
      await api.delete(`/admin/staff/${id}`);
      setStaff((ls) => ls.filter((s) => s.id !== id));
      toast.success("Removed");
    } catch (error) {
      console.error("Staff delete failed:", error);
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="staff-panel" className="space-y-6">
      <div className="border border-primary-brand/30 bg-orange-50/60 p-5 flex items-start gap-3 text-sm text-slate-700">
        <ShieldCheck className="h-5 w-5 text-primary-brand flex-none mt-0.5" />
        <span>
          Staff members sign in at <span className="font-bold">/admin/login</span> with the email and password you set here.
          They get access to the <span className="font-bold">Appointments portal only</span> — quotes, shipments, clients, and careers stay admin-only.
        </span>
      </div>

      <form onSubmit={add} data-testid="staff-create-form" className="border border-slate-200 bg-white p-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <label>
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Name *</div>
          <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={adminInputCls} data-testid="staff-name" />
        </label>
        <label>
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Email *</div>
          <input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={adminInputCls} data-testid="staff-email" />
        </label>
        <label>
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Password * (min 6)</div>
          <input type="text" required minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={adminInputCls} data-testid="staff-password" />
        </label>
        <button type="submit" disabled={saving} data-testid="staff-create-btn"
          className="inline-flex items-center justify-center gap-2 bg-primary-brand hover:bg-[#E04F00] disabled:opacity-60 text-white px-5 py-3 font-bold uppercase tracking-wider text-xs">
          <UserPlus className="h-4 w-4" /> {saving ? "Adding…" : "Add Staff"}
        </button>
      </form>

      <div className="border border-slate-200 bg-white">
        {staff.length === 0 ? (
          <EmptyState label="No staff accounts yet. Add your first team member above." />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-slate-500 border-b border-slate-200">
                <th className="px-4 py-3">Added</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Access</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} data-testid={`staff-row-${s.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-4 text-slate-500 text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-4 font-heading font-bold text-slate-900">{s.name}</td>
                  <td className="px-4 py-4 text-slate-700">{s.email}</td>
                  <td className="px-4 py-4"><span className="text-xs uppercase tracking-[0.2em] font-bold text-primary-brand">Appointments</span></td>
                  <td className="px-4 py-4 text-right">
                    <button onClick={() => remove(s.id)} data-testid={`staff-delete-${s.id}`} className="text-slate-500 hover:text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
